import React, { Component } from "react";

// Mirrors OBS_admin_BE/api/services/address_validation_engine.js mode presets.
const VALID_MODES = ["strict", "moderate", "loose", "custom"];
const ALL_GRANULARITIES = [
  "SUB_PREMISE",
  "PREMISE",
  "PREMISE_PROXIMITY",
  "BLOCK",
  "ROUTE",
  "OTHER",
];
const ALL_DPV = ["Y", "S", "D", "N"];
const ALL_CONFIRMED_COMPONENTS = [
  "street_number",
  "route",
  "locality",
  "administrative_area_level_1",
  "postal_code",
  "subpremise",
  "country",
];

const STRICT_RULES = {
  requireCompleteAddress: true,
  allowInferredComponents: false,
  allowReplacedComponents: false,
  allowedGranularity: ["PREMISE", "SUB_PREMISE"],
  requireGeocode: true,
  requireUSPSValidation: false,
  allowedDPV: ["Y"],
  blockIfVacant: true,
  blockIfNoStat: true,
  requireBusinessAddress: false,
  requireConfirmedComponents: [
    "street_number",
    "route",
    "locality",
    "administrative_area_level_1",
    "postal_code",
  ],
  blockOnSpellCorrected: true,
  blockOnReplaced: true,
};

const MODERATE_RULES = {
  requireCompleteAddress: true,
  allowInferredComponents: true,
  allowReplacedComponents: true,
  allowedGranularity: ["PREMISE", "SUB_PREMISE", "PREMISE_PROXIMITY", "BLOCK"],
  requireGeocode: true,
  requireUSPSValidation: false,
  allowedDPV: ["Y", "S", "D"],
  blockIfVacant: true,
  blockIfNoStat: false,
  requireBusinessAddress: false,
  requireConfirmedComponents: [
    "route",
    "locality",
    "administrative_area_level_1",
    "postal_code",
  ],
  blockOnSpellCorrected: false,
  blockOnReplaced: false,
};

const LOOSE_RULES = {
  requireCompleteAddress: false,
  allowInferredComponents: true,
  allowReplacedComponents: true,
  allowedGranularity: ALL_GRANULARITIES.slice(),
  requireGeocode: false,
  requireUSPSValidation: false,
  allowedDPV: ALL_DPV.slice(),
  blockIfVacant: false,
  blockIfNoStat: false,
  requireBusinessAddress: false,
  requireConfirmedComponents: [],
  blockOnSpellCorrected: false,
  blockOnReplaced: false,
};

const MODE_PRESETS = {
  strict: STRICT_RULES,
  moderate: MODERATE_RULES,
  loose: LOOSE_RULES,
};

export const DEFAULT_ADDRESS_CONFIG = {
  enabled: false,
  mode: "strict",
  blockOnFail: true,
  countryRules: {},
  rules: { ...STRICT_RULES },
};

/**
 * Normalize a config value coming from BE (could be JSON string, object, or null).
 */
export function normalizeAddressConfig(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      parsed = null;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return { ...DEFAULT_ADDRESS_CONFIG, rules: { ...STRICT_RULES } };
  }
  const mode = VALID_MODES.includes(parsed.mode) ? parsed.mode : "strict";
  const presetRules = MODE_PRESETS[mode] || STRICT_RULES;
  const rules =
    parsed.rules && typeof parsed.rules === "object"
      ? { ...presetRules, ...parsed.rules }
      : { ...presetRules };
  return {
    enabled: !!parsed.enabled,
    mode,
    blockOnFail: parsed.blockOnFail !== false,
    countryRules: parsed.countryRules || {},
    rules,
  };
}

/**
 * Reusable advanced address-validation config UI block.
 * Props:
 *  - config: object (controlled)
 *  - onChange: (newConfig) => void
 *  - scopeLabel: "Client" | "Agent"  (used in helper text)
 *  - disabled: bool
 *  - addressRequirement: "required" | "optional" | "mixed" | "always" | null
 *      Describes whether address fields are required at registration time.
 *      - "required": every address field is required → validation always runs
 *      - "optional": every address field is optional → validation runs only if user enters one
 *      - "mixed":   some fields required, some optional → validation runs when any field is entered
 *      - "always":  scope has no concept of "optional address" (e.g. agents created by admin)
 *      - null/undefined → banner is not shown
 *  - registrationSettingsHref: optional string href; when provided the banner
 *      links to the registration settings page so admins can adjust it.
 */

// User-friendly labels for the technical Google API terms.
const COMPONENT_LABELS = {
  street_number: "Street number",
  route: "Street name",
  locality: "City",
  administrative_area_level_1: "State / Province",
  postal_code: "ZIP / Postal code",
  subpremise: "Apartment / Suite",
  country: "Country",
};

const DPV_LABELS = {
  Y: "Confirmed deliverable",
  S: "Confirmed (without apartment)",
  D: "Confirmed (vacant lot)",
  N: "Not deliverable",
};

const GRANULARITY_LABELS = {
  SUB_PREMISE: "Apartment / unit level",
  PREMISE: "Building level",
  PREMISE_PROXIMITY: "Near a building",
  BLOCK: "Block level",
  ROUTE: "Street only (no number)",
  OTHER: "City or wider only",
};

const PRESET_DESCRIPTIONS = {
  strict: {
    title: "Strict",
    blurb:
      "Only accept fully verified addresses. Best for shipping or compliance.",
  },
  moderate: {
    title: "Moderate",
    blurb:
      "Accept most real addresses; allow minor corrections and inferred parts. Recommended for most use cases.",
  },
  loose: {
    title: "Loose",
    blurb:
      "Accept almost anything Google can find. Best when you only need a rough address on file.",
  },
  custom: {
    title: "Custom",
    blurb:
      "Mix and match individual rules below. Auto-selected when you tweak any advanced setting.",
  },
};

class AddressValidationConfig extends Component {
  constructor(props) {
    super(props);
    this.state = { showAdvanced: false, showDeveloper: false };
  }

  setMode = (mode) => {
    const { config, onChange } = this.props;
    if (mode === "custom") {
      onChange({ ...config, mode });
      return;
    }
    const presetRules = MODE_PRESETS[mode] || STRICT_RULES;
    onChange({ ...config, mode, rules: { ...presetRules } });
  };

  setBlockOnFail = (value) => {
    const { config, onChange } = this.props;
    onChange({ ...config, blockOnFail: !!value });
  };

  setRule = (key, value) => {
    const { config, onChange } = this.props;
    onChange({
      ...config,
      mode: "custom",
      rules: { ...config.rules, [key]: value },
    });
  };

  toggleArrayMember = (key, value, checked) => {
    const { config } = this.props;
    const cur = Array.isArray(config.rules[key]) ? config.rules[key] : [];
    const next = checked
      ? Array.from(new Set([...cur, value]))
      : cur.filter((x) => x !== value);
    this.setRule(key, next);
  };

  toggleAdvanced = (e) => {
    e.preventDefault();
    this.setState((s) => ({ showAdvanced: !s.showAdvanced }));
  };

  toggleDeveloper = (e) => {
    e.preventDefault();
    this.setState((s) => ({ showDeveloper: !s.showDeveloper }));
  };

  renderPresetCard = (mode) => {
    const { config } = this.props;
    const meta = PRESET_DESCRIPTIONS[mode];
    return (
      <div className="col-md-3 col-sm-6" key={mode}>
        {this.renderChoiceCard({
          active: config.mode === mode,
          onSelect: () => this.setMode(mode),
          title: meta.title,
          description: meta.blurb,
        })}
      </div>
    );
  };

  renderPlainCheckbox = (id, checked, onChange, label) => {
    const { disabled } = this.props;
    return (
      <label
        htmlFor={id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: 0,
          padding: "4px 0",
          fontSize: 13,
          color: "#364a63",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          id={id}
          checked={!!checked}
          onChange={onChange}
          disabled={disabled}
          style={{
            width: 16,
            height: 16,
            margin: 0,
            flexShrink: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            accentColor: "#6576ff",
          }}
        />
        <span style={{ lineHeight: 1.4 }}>{label}</span>
      </label>
    );
  };

  renderChoiceCard = ({ active, onSelect, title, description }) => {    const { disabled } = this.props;
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-pressed={active}
        aria-disabled={disabled}
        onClick={() => !disabled && onSelect()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onSelect();
          }
        }}
        style={{
          position: "relative",
          border: active ? "2px solid #6576ff" : "1px solid #dbdfea",
          background: active ? "#eef0ff" : "#fff",
          borderRadius: 6,
          height: "100%",
          padding: "16px 40px 16px 16px",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "border-color 0.15s ease, background 0.15s ease",
        }}
      >
        <em
          className={
            "icon " + (active ? "ni ni-check-circle-fill" : "ni ni-circle")
          }
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            color: active ? "#6576ff" : "#b6c6e3",
            fontSize: 18,
            lineHeight: 1,
          }}
        ></em>
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: "#364a63",
            lineHeight: 1.3,
            marginBottom: 6,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#8094ae",
            fontWeight: 400,
            lineHeight: 1.5,
          }}
        >
          {description}
        </div>
      </div>
    );
  };

  renderToggle = (id, checked, onChange, label, description) => {
    const { disabled } = this.props;
    return (
      <div
        className="d-flex justify-content-between align-items-start py-2"
        style={{ borderBottom: "1px solid #f0f3f8" }}
      >
        <div style={{ flex: 1, paddingRight: 12 }}>
          <label
            htmlFor={id}
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#364a63",
              marginBottom: 2,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            {label}
          </label>
          {description ? (
            <div style={{ fontSize: 12, color: "#8094ae" }}>{description}</div>
          ) : null}
        </div>
        <div className="custom-control custom-switch">
          <input
            type="checkbox"
            className="custom-control-input"
            id={id}
            checked={!!checked}
            onChange={onChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor={id}></label>
        </div>
      </div>
    );
  };

  render() {
    const { config, scopeLabel, disabled, addressRequirement, registrationSettingsHref } = this.props;
    const r = config.rules || STRICT_RULES;
    const { showAdvanced, showDeveloper } = this.state;
    const isCustom = config.mode === "custom";

    // Build the info banner copy describing the relationship between
    // registration-required-fields and address validation.
    let banner = null;
    if (addressRequirement) {
      const link = registrationSettingsHref ? (
        <a
          href={registrationSettingsHref}
          style={{ marginLeft: 4, fontWeight: 500 }}
        >
          Manage registration fields
        </a>
      ) : null;
      const lcScope = scopeLabel ? scopeLabel.toLowerCase() : "user";
      if (addressRequirement === "required") {
        banner = {
          tone: "info",
          icon: "ni ni-check-circle",
          text: (
            <>
              Address is currently <strong>required</strong> at {lcScope}{" "}
              registration. These rules will run on every signup and profile
              update.
              {link}
            </>
          ),
        };
      } else if (addressRequirement === "optional") {
        banner = {
          tone: "warning",
          icon: "ni ni-info",
          text: (
            <>
              Address is currently <strong>optional</strong> at {lcScope}{" "}
              registration. These rules will run <strong>only when the {lcScope} enters an address</strong>; empty addresses are accepted.
              {link}
            </>
          ),
        };
      } else if (addressRequirement === "mixed") {
        banner = {
          tone: "warning",
          icon: "ni ni-info",
          text: (
            <>
              Some address fields are required and others are optional at{" "}
              {lcScope} registration. These rules will run whenever any address
              field is entered.
              {link}
            </>
          ),
        };
      } else if (addressRequirement === "always") {
        banner = {
          tone: "info",
          icon: "ni ni-check-circle",
          text: (
            <>
              {scopeLabel || "User"} addresses are always collected. These rules
              will run on every save.
            </>
          ),
        };
      }
    }

    return (
      <div
        className="card card-bordered mt-2"
        style={{ background: "#f8f9fb" }}
      >
        <div className="card-inner">
          <div className="mb-3">
            <h6 className="title mb-1" style={{ fontSize: 14 }}>
              {scopeLabel || ""} Address Validation Settings
            </h6>
            <div style={{ fontSize: 12, color: "#8094ae" }}>
              Choose how strictly addresses entered for{" "}
              {scopeLabel ? scopeLabel.toLowerCase() + "s" : "users"} should be
              checked.
            </div>
          </div>

          {banner ? (
            <div
              className={
                "alert py-2 px-3 d-flex align-items-start " +
                (banner.tone === "warning"
                  ? "alert-warning"
                  : "alert-light")
              }
              style={{ fontSize: 12, marginBottom: 16 }}
            >
              <em
                className={"icon mr-2 mt-1 " + banner.icon}
                style={{ fontSize: 14 }}
              ></em>
              <div>{banner.text}</div>
            </div>
          ) : null}

          {/* Step 1: Strictness preset */}
          <div className="mb-3">
            <label
              className="form-label"
              style={{ fontSize: 13, fontWeight: 600 }}
            >
              1. How strict should validation be?
            </label>
            <div className="row g-2">
              {["strict", "moderate", "loose", "custom"].map((m) =>
                this.renderPresetCard(m)
              )}
            </div>
            {isCustom ? (
              <div
                className="alert alert-light mt-2 py-2 px-3"
                style={{ fontSize: 12, marginBottom: 0 }}
              >
                <em className="icon ni ni-info mr-1"></em>
                You're using <strong>Custom</strong> rules. Adjust them in the
                advanced section below, or pick a preset above to reset.
              </div>
            ) : null}
          </div>

          {/* Step 2: Failure behavior */}
          <div className="mb-2">
            <label
              className="form-label"
              style={{ fontSize: 13, fontWeight: 600 }}
            >
              2. What should happen if an address fails validation?
            </label>
            <div className="row g-2">
              <div className="col-md-6">
                {this.renderChoiceCard({
                  active: config.blockOnFail !== false,
                  onSelect: () => this.setBlockOnFail(true),
                  title: "Block submission",
                  description:
                    "The user cannot save until they fix the address.",
                })}
              </div>
              <div className="col-md-6">
                {this.renderChoiceCard({
                  active: config.blockOnFail === false,
                  onSelect: () => this.setBlockOnFail(false),
                  title: "Warn only",
                  description: "Show a warning, but let the user continue.",
                })}
              </div>
            </div>
          </div>

          {/* Advanced toggle */}
          <div className="mt-3">
            <a
              href="#advanced"
              className="text-primary"
              onClick={this.toggleAdvanced}
              style={{ fontSize: 13 }}
            >
              <em
                className={
                  "icon mr-1 " +
                  (showAdvanced ? "ni ni-chevron-up" : "ni ni-chevron-down")
                }
              ></em>
              {showAdvanced ? "Hide" : "Show"} advanced settings
            </a>
          </div>

          {showAdvanced ? (
            <div className="mt-3">
              {/* Acceptance rules */}
              <div className="mb-3">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#8094ae",
                    marginBottom: 6,
                  }}
                >
                  Acceptance rules
                </div>
                {this.renderToggle(
                  "rl_requireComplete",
                  r.requireCompleteAddress,
                  (e) =>
                    this.setRule("requireCompleteAddress", e.target.checked),
                  "Require a complete address",
                  "Reject if the address is missing parts (e.g. no street number)."
                )}
                {this.renderToggle(
                  "rl_allowInferred",
                  r.allowInferredComponents,
                  (e) =>
                    this.setRule("allowInferredComponents", e.target.checked),
                  "Allow Google to fill in missing parts",
                  "Example: user entered a street and city; Google adds the ZIP code."
                )}
                {this.renderToggle(
                  "rl_allowReplaced",
                  r.allowReplacedComponents,
                  (e) =>
                    this.setRule("allowReplacedComponents", e.target.checked),
                  "Allow Google to fix entered values",
                  "Example: user typed the wrong ZIP and Google substitutes the correct one."
                )}
                {this.renderToggle(
                  "rl_blockSpell",
                  r.blockOnSpellCorrected,
                  (e) =>
                    this.setRule("blockOnSpellCorrected", e.target.checked),
                  "Reject spelling corrections",
                  "If turned on, addresses where Google fixed a typo will be rejected."
                )}
                {this.renderToggle(
                  "rl_blockReplaced",
                  r.blockOnReplaced,
                  (e) => this.setRule("blockOnReplaced", e.target.checked),
                  "Reject replaced values",
                  "Similar to above, but for full value replacements (not just typos)."
                )}
              </div>

              {/* USPS rules */}
              <div className="mb-3">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#8094ae",
                    marginBottom: 6,
                  }}
                >
                  USPS checks (US addresses only)
                </div>
                {this.renderToggle(
                  "rl_requireUsps",
                  r.requireUSPSValidation,
                  (e) =>
                    this.setRule("requireUSPSValidation", e.target.checked),
                  "Require USPS confirmation",
                  "Reject the address if USPS cannot confirm it."
                )}
                {this.renderToggle(
                  "rl_blockVacant",
                  r.blockIfVacant,
                  (e) => this.setRule("blockIfVacant", e.target.checked),
                  "Reject vacant addresses",
                  "USPS marks addresses as vacant when no one currently lives or works there."
                )}
                {this.renderToggle(
                  "rl_blockNoStat",
                  r.blockIfNoStat,
                  (e) => this.setRule("blockIfNoStat", e.target.checked),
                  "Reject addresses with no delivery history",
                  "USPS has no delivery statistics for the address (often new construction)."
                )}
                {this.renderToggle(
                  "rl_requireBiz",
                  r.requireBusinessAddress,
                  (e) =>
                    this.setRule("requireBusinessAddress", e.target.checked),
                  "Only accept business addresses",
                  "Reject residential addresses."
                )}
              </div>

              {/* Required parts */}
              <div className="mb-3">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#8094ae",
                    marginBottom: 6,
                  }}
                >
                  Address parts that must be confirmed
                </div>
                <div
                  style={{ fontSize: 12, color: "#8094ae", marginBottom: 8 }}
                >
                  Each selected part must be present and confirmed by Google.
                </div>
                <div className="row g-2">
                  {ALL_CONFIRMED_COMPONENTS.map((c) => (
                    <div className="col-md-6 col-lg-4" key={c}>
                      {this.renderPlainCheckbox(
                        "cc_" + c,
                        Array.isArray(r.requireConfirmedComponents) &&
                          r.requireConfirmedComponents.includes(c),
                        (e) =>
                          this.toggleArrayMember(
                            "requireConfirmedComponents",
                            c,
                            e.target.checked
                          ),
                        COMPONENT_LABELS[c] || c
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Developer settings */}
              <div className="mt-3">
                <a
                  href="#developer"
                  className="text-secondary"
                  onClick={this.toggleDeveloper}
                  style={{ fontSize: 12 }}
                >
                  <em
                    className={
                      "icon mr-1 " +
                      (showDeveloper
                        ? "ni ni-chevron-up"
                        : "ni ni-chevron-down")
                    }
                  ></em>
                  {showDeveloper ? "Hide" : "Show"} developer settings
                </a>
              </div>

              {showDeveloper ? (
                <div className="mt-2 p-3" style={{ background: "#fff", borderRadius: 6 }}>
                  <div
                    style={{ fontSize: 12, color: "#8094ae", marginBottom: 12 }}
                  >
                    These map directly to Google Address Validation API fields.
                    Only change if you know what they mean.
                  </div>

                  {this.renderToggle(
                    "rl_requireGeo",
                    r.requireGeocode,
                    (e) => this.setRule("requireGeocode", e.target.checked),
                    "Require geocode (lat/long)",
                    "Reject if Google cannot pinpoint the address on a map."
                  )}

                  <div className="mt-3">
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                      Acceptable USPS DPV codes
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#8094ae", marginBottom: 6 }}
                    >
                      Which USPS Delivery Point Validation results to accept.
                    </div>
                    {ALL_DPV.map((d) =>
                      this.renderPlainCheckbox(
                        "dpv_" + d,
                        Array.isArray(r.allowedDPV) && r.allowedDPV.includes(d),
                        (e) =>
                          this.toggleArrayMember(
                            "allowedDPV",
                            d,
                            e.target.checked
                          ),
                        <>
                          <strong>{d}</strong> &mdash; {DPV_LABELS[d] || d}
                        </>
                      )
                    )}
                  </div>

                  <div className="mt-3">
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                      Acceptable address precision
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#8094ae", marginBottom: 6 }}
                    >
                      How precise the matched address must be.
                    </div>
                    {ALL_GRANULARITIES.map((g) =>
                      this.renderPlainCheckbox(
                        "gr_" + g,
                        Array.isArray(r.allowedGranularity) &&
                          r.allowedGranularity.includes(g),
                        (e) =>
                          this.toggleArrayMember(
                            "allowedGranularity",
                            g,
                            e.target.checked
                          ),
                        GRANULARITY_LABELS[g] || g
                      )
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default AddressValidationConfig;

